import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Switch,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/config';
import { REQUEST_HEADERS } from '../constants/headers';

interface AIFunction {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  customer_id: string;
  input_types: string[];
  allowed_models: string[];
  response_format?: any;
  metadata?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`code-tabpanel-${index}`}
      aria-labelledby={`code-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const FunctionCodeExamples: React.FC = () => {
  const { functionId } = useParams<{ functionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [functionData, setFunctionData] = useState<AIFunction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedInputType, setSelectedInputType] = useState<string>('text');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [apiKeys, setApiKeys] = useState<{ id: number; name?: string; api_key?: string }[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<string>('YOUR_AUTH_TOKEN');
  const [threadMode, setThreadMode] = useState(false);
  const [formDataMode, setFormDataMode] = useState(false);
  const [vectorSearchEnabled, setVectorSearchEnabled] = useState(false);
  const [categories, setCategories] = useState<Array<{id: string, name: string}>>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  useEffect(() => {
    loadFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId]);

  useEffect(() => {
    const loadKeys = async () => {
      try {
        if (!user) return;
        const idToken = await user.getIdToken();
        const res = await fetch('/api/v2/api-keys/', { headers: { Authorization: `Bearer ${idToken}` } });
        if (!res.ok) return;
        const data = await res.json();
        setApiKeys(data || []);
        if ((data || []).length > 0) setSelectedApiKey(data[0].api_key || 'YOUR_AUTH_TOKEN');
      } catch {}
    };
    loadKeys();
  }, [user]);

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (!user || !functionData) return;
        const idToken = await user.getIdToken();
        
        const response = await fetch(`/api/v2/customers/${functionData.customer_id}/categories`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCategories(data.data?.items || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    if (user && functionData) {
      fetchCategories();
    }
  }, [user, functionData]);

  // Auto-apply metadata settings when function loads
  useEffect(() => {
    if (functionData && functionData.metadata) {
      const metadata = typeof functionData.metadata === 'string' 
        ? JSON.parse(functionData.metadata) 
        : functionData.metadata;
      
      // Set thread mode based on chatbot metadata
      if (metadata.chatbot === true) {
        setThreadMode(true);
      }
      
      // Set vector search based on knowledge_categories metadata
      if (metadata.knowledge_categories && Array.isArray(metadata.knowledge_categories) && metadata.knowledge_categories.length > 0) {
        setVectorSearchEnabled(true);
        setSelectedCategories(metadata.knowledge_categories as string[]);
      }
    }
  }, [functionData]);

  const loadFunction = async () => {
    if (!user || !functionId) return;

    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      const response = await fetch(`/api/v2/ai-functions/${functionId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load function');
      }

      const data = await response.json();
      const func = data.data || data;
      setFunctionData(func);
      setSelectedInputType(func.input_types[0] || 'text');
      setSelectedModel(func.allowed_models[0] || '');
    } catch (err) {
      console.error('Error loading function:', err);
      setError('Failed to load function details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const generateInputExample = (inputType: string, model: string) => {
    // For form data mode, return field descriptions
    if (formDataMode) {
      let metadata: any = { source: "api", version: "1.0" };
      
      // Add vector search metadata if enabled
      if (vectorSearchEnabled && selectedCategories.length > 0) {
        metadata.vector_search = {
          enabled: true,
          category_ids: selectedCategories,
          top_k: 8
        };
      }
      
      return {
        _fields: "Form fields to send:",
        input: "File or text content (required)",
        input1: "(Optional) Second file or text",
        input2: "(Optional) Third file or text",
        input_type: inputType,
        model: model,
        message: "(Optional) Instructions or context",
        metadata: JSON.stringify(metadata)
      };
    }

    // Build base example
    let example: any;
    switch (inputType) {
      case 'text':
        example = {
          input: "Enter your text here",
          input_type: "text",
          model: model
        };
        break;
      case 'json':
        example = {
          input: { key: "value", data: "example" },
          input_type: "json",
          model: model
        };
        break;
      case 'image':
      case 'video':
      case 'audio':
      case 'pdf':
      case 'file':
        example = {
          input: "base64_encoded_file_data_here",
          input_type: inputType,
          model: model,
          _comment: `For ${inputType} files, encode the file to base64 and include as a data URL`
        };
        break;
      default:
        example = {
          input: "your_input_here",
          input_type: inputType,
          model: model
        };
    }

    // Add vector search metadata if enabled
    if (vectorSearchEnabled && selectedCategories.length > 0) {
      example.metadata = {
        vector_search: {
          enabled: true,
          category_ids: selectedCategories,
          top_k: 8
        }
      };
    }

    return example;
  };

  const generateCodeSnippets = () => {
    if (!functionData) return {} as Record<string, string>;

    const config = getConfig();
    const baseUrl = config.apiBaseUrl;
    const apiPath = baseUrl.endsWith('/api') ? '' : '/api';
    const endpoint = formDataMode 
      ? `${baseUrl}${apiPath}/v2/functions/${functionData.customer_id}/${functionData.name}/form`
      : `${baseUrl}${apiPath}/v2/functions/${functionData.customer_id}/${functionData.name}`;
    const inputExample = generateInputExample(selectedInputType, selectedModel);
    
    // Base64 file example for non-text types
    const fileHandlingNote = selectedInputType !== 'text' && selectedInputType !== 'json' ? `
# For ${selectedInputType} files, read and encode to base64:
# with open('your_file.${selectedInputType === 'image' ? 'jpg' : selectedInputType}', 'rb') as file:
#     file_data = base64.b64encode(file.read()).decode()
#     data['input'] = f"data:${selectedInputType === 'image' ? 'image/jpeg' : selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType + '/*'};base64,{file_data}"
#
# Note: Files > 10MB are automatically uploaded to GCS for efficiency.
# The API handles this transparently - just send the base64 data.
` : '';

    // Build the curl command
    let curlCommand;
    if (formDataMode) {
      // Form data curl command
      curlCommand = `curl \\
  -X POST \\
  '${endpoint}' \\
  -H 'Authorization: Bearer ${selectedApiKey}' \\${threadMode ? `
  -H 'EASYAI-THREAD-ID: thread-${Date.now()}-example' \\
  -H 'EASYAI-MAX-THREAD-LEN: 10' \\` : ''}
  -F 'input=@/path/to/your/file.${selectedInputType === 'image' ? 'jpg' : selectedInputType === 'pdf' ? 'pdf' : selectedInputType}' \\
  -F 'input1=@/path/to/second/file.jpg' \\
  -F 'input2=@/path/to/third/file.pdf' \\
  -F 'input_type=${selectedInputType}' \\
  -F 'model=${selectedModel}' \\
  -F 'message=Your instructions here' \\
  -F 'metadata=${inputExample.metadata}'

# For text inputs instead of files:
# -F 'input=Your text content here'`;
    } else {
      // JSON curl command
      curlCommand = threadMode 
        ? `curl \\
  -X POST \\
  '${endpoint}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer ${selectedApiKey}' \\
  -H 'EASYAI-THREAD-ID: thread-${Date.now()}-example' \\
  -H 'EASYAI-MAX-THREAD-LEN: 10' \\
  -d '${JSON.stringify(inputExample, null, 2)}'`
        : `curl \\
  -X POST \\
  '${endpoint}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer ${selectedApiKey}' \\
  -d '${JSON.stringify(inputExample, null, 2)}'`;
    }

    return {
      curl: curlCommand,
      
      python: formDataMode ? `import requests
import json

url = "${endpoint}"
headers = {
    "Authorization": "Bearer ${selectedApiKey}"${threadMode ? ',\n    "EASYAI-THREAD-ID": "thread-' + Date.now() + '-example",\n    "EASYAI-MAX-THREAD-LEN": "10"' : ''}
}

# REPLACE FILE PATHS WITH YOUR ACTUAL FILES
file1_path = '/path/to/file1.${selectedInputType === 'image' ? 'jpg' : selectedInputType}'  # <- REPLACE
file2_path = '/path/to/file2.jpg'  # <- REPLACE (optional)
file3_path = '/path/to/file3.pdf'  # <- REPLACE (optional)

# Multiple file upload example
files = [
    ('input', ('file1.${selectedInputType === 'image' ? 'jpg' : selectedInputType}', open(file1_path, 'rb'), '${selectedInputType === 'image' ? 'image/jpeg' : selectedInputType === 'pdf' ? 'application/pdf' : 'application/octet-stream'}')),
    # Uncomment for multiple files:
    # ('input1', ('file2.jpg', open(file2_path, 'rb'), 'image/jpeg')),
    # ('input2', ('file3.pdf', open(file3_path, 'rb'), 'application/pdf'))
]

data = {
    'input_type': '${selectedInputType}',
    'model': '${selectedModel}',
    'message': 'Your instructions here',
    'metadata': ${inputExample.metadata}
}

response = requests.post(url, headers=headers, files=files, data=data)

# Usage tracking - check response headers
print(f"Input tokens: {response.headers.get('${REQUEST_HEADERS.INPUT_TOKENS}')}")
print(f"Output tokens: {response.headers.get('${REQUEST_HEADERS.OUTPUT_TOKENS}')}")
print(f"Credits used: {response.headers.get('${REQUEST_HEADERS.CREDITS_USED}')}")

result = response.json()
print(json.dumps(result, indent=2))

# Remember to close the files
for _, file_tuple in files:
    file_tuple[1].close()

# For text inputs instead of files:
# data['input'] = 'Your text content here'
# response = requests.post(url, headers=headers, data=data)` : `import requests
import json
${selectedInputType !== 'text' && selectedInputType !== 'json' ? 'import base64' : ''}

url = "${endpoint}"
headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${selectedApiKey}"${threadMode ? ',\n    "EASYAI-THREAD-ID": "thread-' + Date.now() + '-example",\n    "EASYAI-MAX-THREAD-LEN": "10"' : ''}
}
${fileHandlingNote}
data = ${JSON.stringify(inputExample, null, 4)}

response = requests.post(url, headers=headers, json=data)

# Usage tracking - check response headers
print(f"Input tokens: {response.headers.get('${REQUEST_HEADERS.INPUT_TOKENS}')}")
print(f"Output tokens: {response.headers.get('${REQUEST_HEADERS.OUTPUT_TOKENS}')}")
print(f"Credits used: {response.headers.get('${REQUEST_HEADERS.CREDITS_USED}')}")

result = response.json()
print(json.dumps(result, indent=2))`,
      
      javascript: formDataMode ? `// Multiple file upload with FormData
const formData = new FormData();

// Add files - assumes you have file input elements
const fileInput1 = document.getElementById('file1');
const fileInput2 = document.getElementById('file2');
const fileInput3 = document.getElementById('file3');

if (fileInput1.files[0]) formData.append('input', fileInput1.files[0]);
if (fileInput2.files[0]) formData.append('input1', fileInput2.files[0]);
if (fileInput3.files[0]) formData.append('input2', fileInput3.files[0]);

// Add other fields
formData.append('input_type', '${selectedInputType}');
formData.append('model', '${selectedModel}');
formData.append('message', 'Your instructions here');
formData.append('metadata', ${inputExample.metadata});

const response = await fetch('${endpoint}', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ${selectedApiKey}'${threadMode ? ',\n    \'EASYAI-THREAD-ID\': \'thread-' + Date.now() + '-example\',\n    \'EASYAI-MAX-THREAD-LEN\': \'10\'' : ''}
  },
  body: formData
});

const result = await response.json();
console.log(result);

// For text inputs instead of files:
// formData.append('input', 'Your text content here');` : `const response = await fetch('${endpoint}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ${selectedApiKey}'${threadMode ? ',\n    \'EASYAI-THREAD-ID\': \'thread-' + Date.now() + '-example\',\n    \'EASYAI-MAX-THREAD-LEN\': \'10\'' : ''}
  },
  body: JSON.stringify(${JSON.stringify(inputExample, null, 2)})
});

const result = await response.json();
console.log(result);${selectedInputType !== 'text' && selectedInputType !== 'json' ? `

// For ${selectedInputType} files:
// const file = document.getElementById('fileInput').files[0];
// const reader = new FileReader();
// reader.onloadend = () => {
//   const base64 = reader.result; // This is already a data URL
//   // Use base64 as the input
// };
// reader.readAsDataURL(file);` : ''}`,

      nodejs: formDataMode ? `const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

const url = '${endpoint}';
const form = new FormData();

// Add files - REPLACE WITH YOUR ACTUAL FILE PATHS
const file1Path = '/path/to/file1.${selectedInputType === 'image' ? 'jpg' : selectedInputType}'; // <- REPLACE
const file2Path = '/path/to/file2.jpg'; // <- REPLACE (optional)
const file3Path = '/path/to/file3.pdf'; // <- REPLACE (optional)

form.append('input', fs.createReadStream(file1Path));
// Uncomment for multiple files:
// form.append('input1', fs.createReadStream(file2Path));
// form.append('input2', fs.createReadStream(file3Path));

// Add other fields
form.append('input_type', '${selectedInputType}');
form.append('model', '${selectedModel}');
form.append('message', 'Your instructions here');
form.append('metadata', ${inputExample.metadata});

const headers = {
  ...form.getHeaders(),
  'Authorization': 'Bearer ${selectedApiKey}'${threadMode ? ',\n  \'EASYAI-THREAD-ID\': \'thread-' + Date.now() + '-example\',\n  \'EASYAI-MAX-THREAD-LEN\': \'10\'' : ''}
};

axios.post(url, form, { headers })
  .then(response => {
    console.log(JSON.stringify(response.data, null, 2));
  })
  .catch(error => {
    console.error('Error:', error.response?.data || error.message);
  });

// For text inputs instead of files:
// form.append('input', 'Your text content here');` : `const axios = require('axios');
${selectedInputType !== 'text' && selectedInputType !== 'json' ? "const fs = require('fs');" : ''}

const url = '${endpoint}';
const headers = {
  'Content-Type': 'application/json',
  'Authorization': 'Bearer ${selectedApiKey}'${threadMode ? ',\n  \'EASYAI-THREAD-ID\': \'thread-' + Date.now() + '-example\',\n  \'EASYAI-MAX-THREAD-LEN\': \'10\'' : ''}
};
${selectedInputType !== 'text' && selectedInputType !== 'json' ? `
// For ${selectedInputType} files:
// const fileBuffer = fs.readFileSync('your_file.${selectedInputType === 'image' ? 'jpg' : selectedInputType}');
// const base64 = fileBuffer.toString('base64');
// const dataUrl = \
//   \\
//   'data:${selectedInputType === 'image' ? 'image/jpeg' : selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType + '/*'};base64,' + base64;
` : ''}
const data = ${JSON.stringify(inputExample, null, 2)};

axios.post(url, data, { headers })
  .then(response => {
    console.log(JSON.stringify(response.data, null, 2));
  })
  .catch(error => {
    console.error('Error:', error.response?.data || error.message);
  });`,

      typescript: formDataMode ? `interface FunctionResponse {
  success: boolean;
  result: any;
  metadata?: {
    execution_time?: number;
    model_used?: string;
  };
}

const callFunction = async (files: File[]): Promise<FunctionResponse> => {
  const formData = new FormData();
  
  // Add files with enumerated field names
  files.forEach((file, index) => {
    const fieldName = index === 0 ? 'input' : \`input\${index}\`;
    formData.append(fieldName, file);
  });
  
  // Add other fields
  formData.append('input_type', '${selectedInputType}');
  formData.append('model', '${selectedModel}');
  formData.append('message', 'Your instructions here');
  formData.append('metadata', ${inputExample.metadata});

  const response = await fetch('${endpoint}', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ${selectedApiKey}'${threadMode ? ',\n      \'EASYAI-THREAD-ID\': \'thread-' + Date.now() + '-example\',\n      \'EASYAI-MAX-THREAD-LEN\': \'10\'' : ''}
    },
    body: formData
  });

  if (!response.ok) {
    throw new Error(\`HTTP error! status: \${response.status}\`);
  }

  return await response.json();
};

// Usage with file inputs
const fileInput1 = document.getElementById('file1') as HTMLInputElement;
const fileInput2 = document.getElementById('file2') as HTMLInputElement;
const fileInput3 = document.getElementById('file3') as HTMLInputElement;

const files: File[] = [];
if (fileInput1.files?.[0]) files.push(fileInput1.files[0]);
if (fileInput2.files?.[0]) files.push(fileInput2.files[0]);
if (fileInput3.files?.[0]) files.push(fileInput3.files[0]);

try {
  const result = await callFunction(files);
  console.log(result);
} catch (error) {
  console.error('Error:', error);
}` : `interface FunctionRequest {
  input: ${selectedInputType === 'json' ? 'any' : 'string'};
  input_type: string;
  model: string;
}

interface FunctionResponse {
  success: boolean;
  result: any;
  metadata?: {
    execution_time?: number;
    model_used?: string;
  };
}

const callFunction = async (): Promise<FunctionResponse> => {
  const response = await fetch('${endpoint}', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ${selectedApiKey}'${threadMode ? ',\n      \'EASYAI-THREAD-ID\': \'thread-' + Date.now() + '-example\',\n      \'EASYAI-MAX-THREAD-LEN\': \'10\'' : ''}
    },
    body: JSON.stringify(${JSON.stringify(inputExample, null, 2)})
  });

  if (!response.ok) {
    throw new Error('HTTP error! status: ' + response.status);
  }

  return await response.json();
};

// Usage
try {
  const result = await callFunction();
  console.log(result);
} catch (error) {
  console.error('Error:', error);
}`,

      go: formDataMode ? `package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "io"
    "mime/multipart"
    "net/http"
    "os"
    "path/filepath"
)

func main() {
    url := "${endpoint}"
    
    // Create multipart form
    body := &bytes.Buffer{}
    writer := multipart.NewWriter(body)
    
    // Add files
    files := []string{
        "/path/to/file1.${selectedInputType === 'image' ? 'jpg' : selectedInputType}",
        "/path/to/file2.jpg",
        "/path/to/file3.pdf",
    }
    
    fieldNames := []string{"input", "input1", "input2"}
    
    for i, filePath := range files {
        file, err := os.Open(filePath)
        if err != nil {
            continue
        }
        defer file.Close()
        
        part, err := writer.CreateFormFile(fieldNames[i], filepath.Base(filePath))
        if err != nil {
            continue
        }
        io.Copy(part, file)
    }
    
    // Add other fields
    writer.WriteField("input_type", "${selectedInputType}")
    writer.WriteField("model", "${selectedModel}")
    writer.WriteField("message", "Your instructions here")
    writer.WriteField("metadata", ${inputExample.metadata})
    
    writer.Close()
    
    req, _ := http.NewRequest("POST", url, body)
    req.Header.Set("Content-Type", writer.FormDataContentType())
    req.Header.Set("Authorization", "Bearer ${selectedApiKey}")${threadMode ? `
    req.Header.Set("EASYAI-THREAD-ID", "thread-${Date.now()}-example")
    req.Header.Set("EASYAI-MAX-THREAD-LEN", "10")` : ''}
    
    client := &http.Client{}
    resp, err := client.Do(req)
    if err != nil {
        panic(err)
    }
    defer resp.Body.Close()
    
    var result map[string]interface{}
    json.NewDecoder(resp.Body).Decode(&result)
    
    fmt.Printf("%+v\\n", result)
}` : `package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "net/http"
    ${selectedInputType !== 'text' && selectedInputType !== 'json' ? '"encoding/base64"\n    "io/ioutil"' : ''}
)

func main() {
    url := "${endpoint}"
    ${selectedInputType !== 'text' && selectedInputType !== 'json' ? `
    // For ${selectedInputType} files:
    // fileData, _ := ioutil.ReadFile("your_file.${selectedInputType === 'image' ? 'jpg' : selectedInputType}")
    // base64Data := base64.StdEncoding.EncodeToString(fileData)
    // dataUrl := fmt.Sprintf("data:${selectedInputType === 'image' ? 'image/jpeg' : selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType + '/*'};base64,%s", base64Data)
    ` : ''}
    payload := map[string]interface{}{
        "input": ${selectedInputType === 'json' ? `map[string]interface{}{"key": "value", "data": "example"}` : `"Enter your text here"`},
        "input_type": "${selectedInputType}",
        "model": "${selectedModel}",
    }
    
    jsonData, _ := json.Marshal(payload)
    
    req, _ := http.NewRequest("POST", url, bytes.NewBuffer(jsonData))
    req.Header.Set("Content-Type", "application/json")
    req.Header.Set("Authorization", "Bearer ${selectedApiKey}")${threadMode ? `
    req.Header.Set("EASYAI-THREAD-ID", "thread-${Date.now()}-example")
    req.Header.Set("EASYAI-MAX-THREAD-LEN", "10")` : ''}
    
    client := &http.Client{}
    resp, err := client.Do(req)
    if err != nil {
        panic(err)
    }
    defer resp.Body.Close()
    
    var result map[string]interface{}
    json.NewDecoder(resp.Body).Decode(&result)
    
    fmt.Printf("%+v\n", result)
}`,

      ruby: formDataMode ? `require 'net/http'
require 'json'

uri = URI('${endpoint}')

# Build multipart form data
boundary = "----WebKitFormBoundary7MA4YWxkTrZu0gW"
body = []

# Add files
files = [
  { field: 'input', path: '/path/to/file1.${selectedInputType === 'image' ? 'jpg' : selectedInputType}' },
  { field: 'input1', path: '/path/to/file2.jpg' },
  { field: 'input2', path: '/path/to/file3.pdf' }
]

files.each do |file|
  if File.exist?(file[:path])
    body << "--#{boundary}"
    body << "Content-Disposition: form-data; name=\\"#{file[:field]}\\"; filename=\\"#{File.basename(file[:path])}\\" "
    body << "Content-Type: #{file[:path].end_with?('.pdf') ? 'application/pdf' : 'image/jpeg'}"
    body << ""
    body << File.binread(file[:path])
  end
end

# Add other fields
fields = {
  'input_type' => '${selectedInputType}',
  'model' => '${selectedModel}',
  'message' => 'Your instructions here',
  'metadata' => ${inputExample.metadata}
}

fields.each do |name, value|
  body << "--#{boundary}"
  body << "Content-Disposition: form-data; name=\\"#{name}\\""
  body << ""
  body << value
end

body << "--#{boundary}--"

http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = true if uri.scheme == 'https'

request = Net::HTTP::Post.new(uri)
request['Content-Type'] = "multipart/form-data; boundary=#{boundary}"
request['Authorization'] = 'Bearer ${selectedApiKey}'${threadMode ? `
request['EASYAI-THREAD-ID'] = 'thread-${Date.now()}-example'
request['EASYAI-MAX-THREAD-LEN'] = '10'` : ''}
request.body = body.join("\\r\\n")

response = http.request(request)
result = JSON.parse(response.body)
puts JSON.pretty_generate(result)` : `require 'net/http'
require 'json'
${selectedInputType !== 'text' && selectedInputType !== 'json' ? "require 'base64'" : ''}

uri = URI('${endpoint}')
http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = true if uri.scheme == 'https'

request = Net::HTTP::Post.new(uri)
request['Content-Type'] = 'application/json'
request['Authorization'] = 'Bearer ${selectedApiKey}'${threadMode ? `
request['EASYAI-THREAD-ID'] = 'thread-${Date.now()}-example'
request['EASYAI-MAX-THREAD-LEN'] = '10'` : ''}
${selectedInputType !== 'text' && selectedInputType !== 'json' ? `
# For ${selectedInputType} files:
# file_data = File.binread('your_file.${selectedInputType === 'image' ? 'jpg' : selectedInputType}')
# base64_data = Base64.encode64(file_data)
# data_url = "data:${selectedInputType === 'image' ? 'image/jpeg' : selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType + '/*'};base64,#{base64_data}"
` : ''}
request.body = ${JSON.stringify(inputExample, null, 2)}.to_json

response = http.request(request)
result = JSON.parse(response.body)
puts JSON.pretty_generate(result)`
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !functionData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Function not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const snippets = generateCodeSnippets();
  const languages = ['curl', 'python', 'javascript', 'nodejs', 'typescript', 'go', 'ruby'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography variant="h4">
            Code Examples: {functionData.display_name}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {functionData.description}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Function Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Function Details</Typography>

              {/* Endpoint on its own line */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Endpoint</Typography>
                <Box
                  component="code"
                  sx={{
                    display: 'block',
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1.5,
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {`/api/v2/functions/${functionData.customer_id}/${functionData.name}${formDataMode ? '/form' : ''}`}
                </Box>
              </Box>

              {/* Controls on a separate row */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Input Type</InputLabel>
                    <Select
                      value={selectedInputType}
                      onChange={(e) => setSelectedInputType(e.target.value)}
                      label="Input Type"
                    >
                      {functionData.input_types.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Model</InputLabel>
                    <Select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      label="Model"
                    >
                      {functionData.allowed_models.map(model => (
                        <MenuItem key={model} value={model}>{model}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>API Key</InputLabel>
                    <Select
                      value={selectedApiKey}
                      onChange={(e) => setSelectedApiKey(e.target.value)}
                      label="API Key"
                    >
                      <MenuItem value="YOUR_AUTH_TOKEN">YOUR_AUTH_TOKEN</MenuItem>
                      {apiKeys.map(k => (
                        <MenuItem key={k.id} value={k.api_key || ''}>
                          {(k.name || `Key ${k.id}`) + (k.api_key ? ` — ${k.api_key}` : '')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={threadMode}
                        onChange={(e) => setThreadMode(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ForumIcon fontSize="small" />
                        <Typography variant="body2">
                          Thread Mode - Include conversation context headers
                        </Typography>
                        {threadMode && <Chip label="ON" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />}
                      </Box>
                    }
                  />
                  {threadMode && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Thread mode adds the following headers to maintain conversation context:
                      </Typography>
                      <Typography variant="body2" component="div">
                        <strong>EASYAI-THREAD-ID:</strong> Unique identifier for the conversation thread<br/>
                        <strong>EASYAI-MAX-THREAD-LEN:</strong> Maximum number of messages to keep in context (default: 10)
                      </Typography>
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formDataMode}
                        onChange={(e) => setFormDataMode(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: formDataMode ? 600 : 400 }}>
                          Form Data Mode — Files &amp; Binary Upload
                        </Typography>
                        {formDataMode && <Chip label="ON" size="small" color="warning" sx={{ height: 18, fontSize: '0.7rem' }} />}
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Use for file/binary inputs: sends raw files via multipart/form-data instead of base64 in a JSON body. Best for Postman, curl, or direct file uploads.
                  </Typography>
                  {formDataMode && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Form data mode</strong> — upload files directly (no base64 encoding):
                      </Typography>
                      <Typography variant="body2" component="div">
                        • <strong>input:</strong> First file or text content<br/>
                        • <strong>input1:</strong> Second file (optional)<br/>
                        • <strong>input2:</strong> Third file (optional)<br/>
                        • Continue with input3, input4, etc. as needed<br/>
                        • Endpoint changes to <code>.../form</code> when this is enabled
                      </Typography>
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(255, 248, 240, 0.5)' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={vectorSearchEnabled}
                          onChange={(e) => {
                            setVectorSearchEnabled(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedCategories([]);
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            Enable Vector Search - Augment with relevant embeddings from knowledge base
                          </Typography>
                          {vectorSearchEnabled && <Chip label="ON" size="small" color="success" sx={{ height: 18, fontSize: '0.7rem' }} />}
                        </Box>
                      }
                    />
                    {vectorSearchEnabled && (
                      <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="category-select-label">Select Categories</InputLabel>
                          <Select
                            labelId="category-select-label"
                            multiple
                            value={selectedCategories}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
                            }}
                            input={<OutlinedInput label="Select Categories" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip 
                                    key={value} 
                                    label={categories.find(c => c.id === value)?.name || value}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            )}
                          >
                            {categories.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                <Checkbox checked={selectedCategories.includes(category.id)} />
                                <ListItemText primary={category.name} />
                              </MenuItem>
                            ))}
                            {categories.length === 0 && (
                              <MenuItem disabled>
                                <Typography variant="body2" color="textSecondary">
                                  No categories available
                                </Typography>
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                        {selectedCategories.length > 0 && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                            Will search top 8 relevant chunks from selected categories
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Code Examples */}
        <Grid item xs={12}>
          <Paper>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                {languages.map((lang, index) => (
                  <Tab key={lang} label={lang.toUpperCase()} id={`code-tab-${index}`} />
                ))}
              </Tabs>
            </Box>

            {languages.map((lang, index) => (
              <TabPanel key={lang} value={tabValue} index={index}>
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    onClick={() => handleCopy(snippets[lang as keyof typeof snippets] || '')}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                  >
                    <Tooltip title="Copy to clipboard">
                      <CopyIcon />
                    </Tooltip>
                  </IconButton>
                  <pre style={{
                    backgroundColor: '#f5f5f5',
                    padding: '16px',
                    borderRadius: '8px',
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    maxHeight: '60vh',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    margin: 0,
                  }}>
                    {snippets[lang as keyof typeof snippets]}
                  </pre>
                </Box>
              </TabPanel>)
            )}
          </Paper>
        </Grid>

        {/* Feature Explanation Section - Moved to bottom */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3, backgroundColor: 'rgba(230, 244, 255, 0.5)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              🚀 Platform Features Guide
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🧵 Thread Mode (Conversation Context)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>When to use:</strong> Enable for chatbots, multi-turn conversations, or any function that needs to remember previous interactions.
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                • <code>EASYAI-THREAD-ID</code>: Unique identifier for the conversation (use same ID for related requests)<br/>
                • <code>EASYAI-MAX-THREAD-LEN</code>: Max messages to keep in history (default: 10)
              </Typography>
              <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                💡 The system automatically stores and retrieves conversation history using your thread ID
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                🔍 Vector Search / Knowledge Base
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>When to use:</strong> Automatically augment requests with relevant information from your knowledge base (uploaded documents, PDFs, etc.).
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                • Searches your uploaded documents for relevant context<br/>
                • Returns top 8 most relevant chunks by default<br/>
                • Automatically injected into the AI function's context<br/>
                • Filter by specific categories for targeted search
              </Typography>
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                💡 Upload documents via the Knowledge Base section, then enable this feature to use them
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                📊 Usage Tracking Headers (Response)
              </Typography>
              <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                • <code>{REQUEST_HEADERS.INPUT_TOKENS}</code>: Input tokens consumed<br/>
                • <code>{REQUEST_HEADERS.OUTPUT_TOKENS}</code>: Output tokens generated<br/>
                • <code>{REQUEST_HEADERS.CREDITS_USED}</code>: AI credits charged<br/>
                • <code>{REQUEST_HEADERS.USAGE}</code>: Total tokens (legacy, for backward compatibility)
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopySuccess(false)}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};
